"use client";

import React, { useEffect, useState, Suspense } from 'react';
import Script from 'next/script';
import { usePathname, useSearchParams } from 'next/navigation';

// Enhanced type definitions
interface AnalyticsConfig {
  googleAnalyticsId?: string | null;
  debugMode: boolean;
}

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (command: string, args: any, options?: any) => void;
    dataLayer?: Array<any>;
  }
}

// Inner component that directly uses client hooks
function AnalyticsScriptsInner() {
  // Use the existing Firebase Measurement ID from environment variables
  const NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID = process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID || null;

  // Track route changes for SPA-style analytics
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [analyticsConfig, setAnalyticsConfig] = useState<AnalyticsConfig>({
    googleAnalyticsId: NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
    debugMode: process.env.NODE_ENV === 'development'
  });

  // Safely log debug information
  const debugLog = React.useCallback((message: string, data?: any) => {
    if (analyticsConfig.debugMode) {
      console.log(`[Analytics Debug] ${message}`, data || '');
    }
  }, [analyticsConfig.debugMode]);

  // Track page views on route changes
  useEffect(() => {
    const { googleAnalyticsId } = analyticsConfig;

    if (googleAnalyticsId && typeof window !== 'undefined' && window.gtag) {
      debugLog('Tracking page view:', pathname);

      window.gtag('event', 'page_view', {
        page_path: pathname,
        page_search: searchParams.toString(),
        send_to: googleAnalyticsId
      });
    }
  }, [pathname, searchParams, analyticsConfig, debugLog]);

  // Only render scripts when we have a valid Analytics ID
  if (!analyticsConfig.googleAnalyticsId) {
    debugLog('No valid Analytics ID found. Skipping script injection.');
    return null;
  }

  return (
    <>
      {/* Google Analytics 4 Scripts */}
      <Script
        id="ga-loader"
        src={`https://www.googletagmanager.com/gtag/js?id=${analyticsConfig.googleAnalyticsId}`}
        strategy="afterInteractive"
      />
      <Script
        id="ga-init"
        strategy="afterInteractive"
      >
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${analyticsConfig.googleAnalyticsId}', {
            debug_mode: ${analyticsConfig.debugMode},
            'anonymize_ip': true
          });
        `}
      </Script>
    </>
  );
}

// Main component that wraps the inner component in Suspense
const AnalyticsScripts: React.FC = () => {
  return (
    <Suspense fallback={null}>
      <AnalyticsScriptsInner />
    </Suspense>
  );
};

export default AnalyticsScripts;
